import { alpha, TableCell, useTheme } from "@mui/material";
import React, {Fragment, useRef, useReducer} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import {KtaRow, SalaryTableRow} from "../../types/common";
import OptimizedTextField from "./OptimizedTextField";
import {isKtaRow} from "../helpers";
import {leadingZerosDateFormat} from "../../../../common/helpers/formatDateTime";
import {format, isValid} from "date-fns";

type Props = {
  row: SalaryTableRow;
  index: number;
  salaryKeys: string[];
    onChange?: (row: SalaryTableRow) => void;
};

const SalaryTableCells = ({row, index, salaryKeys, onChange}: Props) => {
  const theme = useTheme();
  const {control} = useFormContext();

  // Manual touched tracking
  const touchedRef = useRef<Record<string, boolean>>({});
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const ktaRow = isKtaRow(row);
  const rowSalary = ktaRow ? row.currentKta : row.raiseDateSalary?.salary;


  const renderDateValue = (date: string) => (s: string): React.ReactNode => (<div>{date}<br/>{s}</div>);

  const createCells = (code: string, rowSalaryValue: string, inputStyle: any, rowValueRenderer?: (s: string) => React.ReactNode) => (
      <Fragment key={code}>
          <TableCell>{rowValueRenderer ? rowValueRenderer(rowSalaryValue) : rowSalaryValue}</TableCell>
          <TableCell>
              <Controller
                  name={`data.${index}.salaryIncrease.${code}`}
                  control={control}
                  render={({field}) => (
                      <NumericFormat
                          name={field.name}
                          value={field.value}
                          onBlur={field.onBlur}
                          getInputRef={field.ref}
                          decimalSeparator=","
                          valueIsNumericString
                          onValueChange={(values) => {
                              field.onChange(values.value);
                              if (!touchedRef.current[code]) {
                                  touchedRef.current[code] = true;
                                  forceUpdate();
                              }
                              if (onChange) {
                                  onChange(row)
                              }
                          }}
                          customInput={OptimizedTextField}
                          sx={inputStyle}
                          inputProps={{style: {textAlign: "center"}}}
                      />
                  )}
              />
          </TableCell>
      </Fragment>
  );

  const createKtaCells = (row: KtaRow, index: number) => {
      const fixedKtaCode = "kta";
      const isTouched = touchedRef.current[fixedKtaCode] ?? false;
      const inputStyle = {
          backgroundColor: isTouched
              ? alpha(theme.palette.error.light, 0.2)
              : "transparent",
          maxWidth: "100px",
      };

      const ktadate = row.salaryIncrease["ktadate"];
      if (!isValid(ktadate)) {
          return createCells(fixedKtaCode, row.currentKeskiansio, inputStyle);
      }
      const date = format(ktadate, leadingZerosDateFormat);
      return createCells(fixedKtaCode, row.currentKeskiansio, inputStyle, renderDateValue(date))
  }

  return (
    <>
        {ktaRow && createKtaCells(row, index)}
      {salaryKeys.map((code) => {
        const isTouched = touchedRef.current[code] ?? false;

        const inputStyle = {
          backgroundColor: isTouched
            ? alpha(theme.palette.error.light, 0.2)
            : "transparent",
          maxWidth: "100px",
        };

        const rowSalaryValue = rowSalary ? (rowSalary[code] || "-") : "?";

          return createCells(code, rowSalaryValue, inputStyle);
      })}
    </>
  );
};

export default SalaryTableCells;
